import _ from 'lodash';
import { ISubscriptionInDTO } from '../../repository/jago/model/input/ISubscriptionInDTO';
import { activationBaselineSubscriptions } from '../activationBaselineSubscriptions';
import { RECURRENT_PAYMENT } from './parser';
import { SubscriptionLogicEnum } from '../../repository/jago/model/input/ISubscriptionLogicEnum';
import {
  IFieldChange,
  IPricelistDiffEntry,
  IPricelistPlan,
  IPricelistRow,
  PricelistDiffStatus,
  PricelistFieldName,
  PRICELIST_DIFF_STATUSES,
} from './types';

export const MAIN_TIER_RESOURCE = 'DOMAIN';

const EDITABLE_FIELDS: PricelistFieldName[] = ['fixedPrice', 'burnRate', 'visible', 'provisioningType', 'requestToQuote', 'canBeDeactivated'];
const READONLY_FIELDS: PricelistFieldName[] = ['name', 'logic', 'maxElements', 'maxUsers', 'maxLocks', 'resource', 'category'];

export const buildKey = (sku: string, mainTierId: number | null | undefined, pendingTierSku?: string): string =>
  `${sku}|${pendingTierSku ? `@${pendingTierSku}` : mainTierId === null || mainTierId === undefined ? '' : mainTierId}`;

export const isMainTier = (s: { resource?: string | null }): boolean => s.resource === MAIN_TIER_RESOURCE;

/** Value of a field as the row wants it, `undefined` meaning "not specified in the file" (never compared). */
function rowValue(row: IPricelistRow, field: PricelistFieldName): IFieldChange['newValue'] {
  return row[field];
}

function existingValue(sub: ISubscriptionInDTO, field: PricelistFieldName): IFieldChange['oldValue'] {
  const value = sub[field];
  switch (field) {
    case 'maxElements':
    case 'maxUsers':
    case 'maxLocks':
    case 'fixedPrice':
    case 'burnRate':
      return Number(value || 0);
    case 'visible':
    case 'requestToQuote':
    case 'canBeDeactivated':
      return Boolean(value);
    case 'name':
      return typeof value === 'string' ? value.trim() : value;
    default:
      return value === null ? undefined : (value as IFieldChange['oldValue']);
  }
}

function diffFields(row: IPricelistRow, sub: ISubscriptionInDTO, fields: PricelistFieldName[]): IFieldChange[] {
  const changes: IFieldChange[] = [];
  fields.forEach(field => {
    const newValue = rowValue(row, field);
    if (newValue === undefined) {
      return;
    }
    const oldValue = existingValue(sub, field);
    if (oldValue !== newValue) {
      changes.push({ field, oldValue, newValue });
    }
  });
  return changes;
}

const describeChange = (c: IFieldChange): string => `${c.field}: ${String(c.oldValue)} -> ${String(c.newValue)}`;

/** SKU structure: <PREFIX: 2 letters + digit><PRODUCT CODE: 3 digits + letter><UNIQUE ID: 3 digits>. */
const SKU_PATTERN = /^([A-Z]{2}\d)(\d{3}[A-Z])(\d{3})$/;
const SKU_PREFIX_PATTERN = /^[A-Z]{2}\d/;

/** Only recurrent COUNT_ELEMENTS plugins are attached to main tiers. */
export const needsMainTier = (row: IPricelistRow): boolean =>
  row.resource !== MAIN_TIER_RESOURCE && row.payment === RECURRENT_PAYMENT && row.logic === SubscriptionLogicEnum.COUNT_ELEMENTS;

export interface IMainTierCandidate {
  sku: string;
  prefix: string;
  productCode: string;
  maxElements: number;
  /** Undefined when the main tier is a DOMAIN row of the same import, not created yet. */
  existing?: ISubscriptionInDTO;
}

export function buildMainTierCandidates(existingTiers: ISubscriptionInDTO[], importRows: IPricelistRow[]): IMainTierCandidate[] {
  const candidates: IMainTierCandidate[] = [];
  const add = (sku: string, maxElements: number, existing?: ISubscriptionInDTO) => {
    const match = SKU_PATTERN.exec(sku.trim().toUpperCase());
    if (match) {
      candidates.push({ sku, prefix: match[1], productCode: match[2], maxElements, existing });
    }
  };
  existingTiers.forEach(t => add(t.sku, t.maxElements, t));
  const existingSkus = new Set(existingTiers.map(t => t.sku));
  importRows
    .filter(r => r.resource === MAIN_TIER_RESOURCE && !existingSkus.has(r.sku))
    .forEach(r => add(r.sku, r.maxElements));
  return candidates;
}

/**
 * Main tier of `type` for a plugin: same product code as the type (per SKU family, IS or SL) and same maxElements.
 * Unique ids in SKUs are not aligned between tiers and plugins, so maxElements is the reliable link.
 * When several tiers match (e.g. SL1 and SL2 versions), the one with the plugin's prefix wins.
 */
export function resolveMainTier(row: IPricelistRow, type: string, candidates: IMainTierCandidate[]): { tier?: IMainTierCandidate; reason?: string } {
  const sku = row.sku.trim().toUpperCase();
  const family = sku.startsWith('IS') ? 'IS' : 'SL';
  const rule = activationBaselineSubscriptions.find(t => t.name === type);
  const code = rule ? rule.skuRule[family] : undefined;
  if (!code) {
    return { reason: `${type} has no product code for the ${family} SKU family` };
  }
  const matches = candidates.filter(c => c.productCode === code && c.maxElements === row.maxElements);
  if (matches.length === 1) {
    return { tier: matches[0] };
  }
  if (matches.length === 0) {
    return { reason: `no ${type} main tier (${code}) with maxElements ${row.maxElements}` };
  }
  const prefixMatch = SKU_PREFIX_PATTERN.exec(sku);
  const samePrefix = prefixMatch ? matches.filter(c => c.prefix === prefixMatch[0]) : [];
  if (samePrefix.length === 1) {
    return { tier: samePrefix[0] };
  }
  return { reason: `ambiguous ${type} main tier with maxElements ${row.maxElements}: ${matches.map(c => c.sku).join(', ')}` };
}

/**
 * Builds the diff between the rows of the price list (already filtered by company) and the existing
 * subscriptions of the marketplace.
 */
export function planPricelist(rows: IPricelistRow[], existing: ISubscriptionInDTO[]): IPricelistPlan {
  const candidates = buildMainTierCandidates(existing.filter(isMainTier), rows);
  const existingByKey = new Map<string, ISubscriptionInDTO>();
  existing.forEach(s => existingByKey.set(buildKey(s.sku, s.mainTier), s));

  const newEntry = (row: IPricelistRow): IPricelistDiffEntry => ({
    key: '',
    row,
    status: 'UNCHANGED',
    mainTierId: null,
    changes: [],
    readonlyChanges: [],
    errors: [],
    warnings: [...row.warnings],
  });

  const entries: IPricelistDiffEntry[] = [];
  rows.forEach(row => {
    if (!needsMainTier(row)) {
      const entry = newEntry(row);
      if (row.mainTierTypes.length > 0) {
        entry.warnings.push('Main tier subscription types ignored: only recurrent COUNT_ELEMENTS plugins have a main tier');
      }
      entry.key = buildKey(row.sku, null);
      entries.push(entry);
      return;
    }
    const warnings: string[] = [];
    const mapped: IPricelistDiffEntry[] = [];
    if (row.mainTierTypes.length === 0) {
      warnings.push('No main tier subscription types specified');
    }
    row.mainTierTypes.forEach(type => {
      const { tier, reason } = resolveMainTier(row, type, candidates);
      if (!tier) {
        warnings.push(`${type}: ${reason}`);
        return;
      }
      const entry = newEntry(row);
      entry.mainTierType = type;
      if (tier.existing) {
        entry.mainTierId = tier.existing.id;
        entry.mainTierLabel = `${tier.existing.sku} (${tier.existing.name})`;
      } else {
        entry.pendingMainTierSku = tier.sku;
        entry.mainTierLabel = `${tier.sku} (new)`;
      }
      entry.key = buildKey(row.sku, entry.mainTierId, entry.pendingMainTierSku);
      mapped.push(entry);
    });
    if (mapped.length === 0) {
      const skipped = newEntry(row);
      skipped.status = 'SKIPPED';
      skipped.key = `${row.sku}|skipped`;
      skipped.warnings.push(...warnings);
      entries.push(skipped);
      return;
    }
    mapped.forEach(e => e.warnings.push(...warnings));
    entries.push(...mapped);
  });

  // duplicates inside the file
  const byKey = _.groupBy(entries.filter(e => e.errors.length === 0 && e.status !== 'SKIPPED'), e => e.key);
  _.forEach(byKey, group => {
    if (group.length > 1) {
      const rowNumbers = group.map(e => e.row.rowNumber).join(', ');
      group.forEach(e => e.errors.push(`Duplicate subscription in file (rows ${rowNumbers})`));
    }
  });

  entries.forEach(entry => {
    if (entry.status === 'SKIPPED') {
      return;
    }
    if (entry.errors.length > 0) {
      entry.status = 'ERROR';
      return;
    }
    const match = entry.pendingMainTierSku ? undefined : existingByKey.get(entry.key);
    if (!match) {
      entry.status = 'NEW';
      return;
    }
    entry.existing = match;
    entry.changes = diffFields(entry.row, match, EDITABLE_FIELDS);
    entry.readonlyChanges = diffFields(entry.row, match, READONLY_FIELDS);
    if (entry.changes.length > 0) {
      entry.status = 'CHANGED';
    } else if (entry.readonlyChanges.length > 0) {
      entry.status = 'READONLY_CHANGED';
    }
    entry.readonlyChanges.forEach(c => entry.warnings.push(`Not editable, not applied: ${describeChange(c)}`));
  });

  const matchedKeys = new Set(entries.filter(e => e.existing).map(e => e.key));
  const onlyInBackend = existing.filter(s => !matchedKeys.has(buildKey(s.sku, s.mainTier)));

  const counts = {} as Record<PricelistDiffStatus, number>;
  PRICELIST_DIFF_STATUSES.forEach(status => {
    counts[status] = entries.filter(e => e.status === status).length;
  });
  return { entries, onlyInBackend, counts };
}
